import React, { useState, useEffect, useRef } from 'react';
import './NewsFeed.css';
import { getAllNews } from '../services/newsApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { createTweetEmbed } from '../utils/tweetUtils';

const NewsFeed = ({ isOpen, onClose }) => {
  const [render, setRender] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
    } else {
      const timer = setTimeout(() => setRender(false), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      const startTime = Date.now();
      const data = await getAllNews();
      
      const elapsedTime = Date.now() - startTime;
      const minimumLoadTime = 700; 
      
      if (elapsedTime < minimumLoadTime) {
        await new Promise(resolve => setTimeout(resolve, minimumLoadTime - elapsedTime));
      }

      if (data && data.success) {
        setNewsItems(data.news);
        setLastUpdate(new Date());
      } else {
        throw new Error(data && data.message ? data.message : 'Failed to load news data');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();

    const refreshInterval = setInterval(() => {
      fetchNews();
    }, 300000); 

    return () => clearInterval(refreshInterval);
  }, []);
  
  useEffect(() => {
    if (isOpen && newsItems.length > 0) {
      const initTwitterWidgets = () => {
        if (window.twttr && window.twttr.widgets) {
          window.twttr.widgets.load();
        } else {
          setTimeout(initTwitterWidgets, 500);
        }
      };

      initTwitterWidgets();
    }
  }, [newsItems, isOpen, render]);

  const handleRefresh = () => {
    fetchNews();
  };

  const handleNewsClick = (linkUrl) => {
    if (linkUrl) {
      if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
        window.open(linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = linkUrl;
      }
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formattedLastUpdate = lastUpdate
    ? lastUpdate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : '';

  if (!render && !isOpen) {
    return null;
  }

  return (
    <div className={`news-feed-sidebar ${isOpen ? 'open' : ''}`}>
      
      <div className="news-header">
        <h2>Latest News</h2>
        <div className="news-controls">
          <button
            onClick={handleRefresh}
            className={`refresh-btn ${loading ? 'loading' : ''}`}
            disabled={loading}
            title={loading ? 'Refreshing news...' : 'Refresh news'}
          >
            ↻
          </button>
          {lastUpdate && (
            <span className="last-update">Updated {formattedLastUpdate}</span>
          )}
        </div>
      </div>

      {error ? (
        <p className="news-error">{error}</p>
      ) : loading && newsItems.length === 0 ? (
        <div className="news-loading">Loading news...</div>
      ) : newsItems.length === 0 ? (
        <p className="no-news">No news available at the moment.</p>
      ) : (
        <div className="news-list">
          {newsItems.map((item) => (
            <div
              key={item.id}
              className={`news-item ${item.important ? 'important' : ''} ${
                item.linkUrl ? 'clickable' : ''
              }`}
              onClick={() => item.linkUrl && handleNewsClick(item.linkUrl)}
            >
              <div className="news-item-header">
                <h3 className="news-title">{item.title}</h3>
                {item.important && <span className="important-badge">Important</span>}
                <span className="news-date">{formatDate(item.date)}</span>
              </div>

              <div className="news-content">
                <p>{item.content}</p>

                {item.videoEmbed && (
                  <div className="news-video-container">
                    <iframe
                      title={`Video for ${item.title}`}
                      src={item.videoEmbed}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}

                {(item.tweetEmbed || item.tweetUrl) && (
                  <div className="news-tweet-container">
                    {item.tweetEmbed ? (
                      <div
                        className="tweet-embed legacy-embed"
                        dangerouslySetInnerHTML={{ __html: item.tweetEmbed }}
                      />
                    ) : item.tweetUrl && (
                      <div
                        className="tweet-embed url-embed"
                        key={`tweet-${item.id}`}
                        dangerouslySetInnerHTML={{ 
                          __html: createTweetEmbed(item.tweetUrl) 
                        }}
                      />
                    )}
                  </div>
                )}

                {item.linkUrl && (
                  <div className="news-link-indicator">
                    Click to open link <span className="arrow">→</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsFeed;
